import Swal from "sweetalert2";
import { API_BASE_URL } from "@/config";
import { validateToken } from '@/utils/auth';
import { LOGOUT } from "@/utils/logout.js";
import { useToast } from "@/components/Toast/useToast";
import HeaderSection from './components/HeaderSection.vue';
import TabsNavigation from './components/TabsNavigation.vue';
import OverviewTab from './components/OverviewTab.vue';
import ConfigurationsTab from './components/ConfigurationsTab.vue';
import TermsTab from './components/TermsTab.vue';
import ReasonsTab from './components/ReasonsTab.vue';
import RouterTab from './components/RouterTab.vue';
import ConfirmationTab from './components/ConfirmationTab.vue';
import ServicesTab from './components/ServicesTab.vue';
import FooterActions from './components/FooterActions.vue';

export default {
  name: "ViewSingleWorkflow",
  mixins: [LOGOUT],
  components: {
    HeaderSection,
    TabsNavigation,
    OverviewTab,
    ConfigurationsTab,
    ServicesTab,
    TermsTab,
    ReasonsTab,
    RouterTab,
    ConfirmationTab,
    FooterActions
  },
  data() {
    return {
      workflow_data: null,
      active_tab: "overview",
      workflows: [],
      tabs: [
        { label: "Overview", value: "overview" },
        { label: "Configurations", value: "configurations" },
        { label: "Services", value: "services" },
        { label: "Terms & Conditions", value: "terms" },
        { label: "Reasons", value: "reasons" },
        { label: "Router", value: "router" },
        { label: "Confirmation", value: "confirmation" }
      ],
      // Configuration data
      allow_multiple_onboarding: false,
      store_on_api: false,
      on_behalf_of_prepopulation: false,
      allow_batch_onboarding: false,
      workflow_name: "",
      workflow_description: "",
      workflow_status: "",
      // Modal data
      selectedService: null,
      serviceDescription: '',
      serviceOutputs: [],
      serviceInputs: [],
      serviceCost: '0.00',
      // Export workflow
      exporting: false
    };
  },
  computed: {
    terms_status() {
      if (!this.workflow_data || !this.workflow_data.workflow_completed) {
        return {
          class: 'in-completed',
          text: 'Terms and Conditions Not Available'
        };
      }

      const { consent_provided, selected_use_terms_and_conditions } = this.workflow_data.workflow_completed;
      const is_completed = consent_provided || selected_use_terms_and_conditions;

      return {
        class: is_completed ? 'completed' : 'in-completed',
        text: consent_provided
          ? 'Terms and Conditions Uploaded'
          : 'Verify Terms and Conditions Used'
      };
    },

    internal_workflows() {
      return this.workflows.filter(w => w.sub_workflow_type === "internal");
    },
    external_workflows() {
      return this.workflows.filter(w => w.sub_workflow_type === "external");
    }
  },
  methods: {
    on_tab_changed(tab_value) {
      this.active_tab = tab_value;
    },

    open_modal(service) {
      this.selectedService = service;
      $('#service_description_label').text(service.service_name);
      this.display_custom_service_details(service.id);
      $('#service_description_modal').modal('show');
    },

    display_custom_service_details(service_id) {
      const token = localStorage.getItem('token');
      const form_details_api = `${API_BASE_URL}/verify_api/service_management_api/get_custom_form_details/${service_id}/`;
      
      this.serviceDescription = 'Loading form details...';
      this.serviceCost = '';
      this.serviceInputs = [];
      this.serviceOutputs = [];
      
      $.ajax({
        url: form_details_api,
        method: 'GET',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json'
        },
        success: (response) => {
          
          const questions = response.questions || [];
      
          this.serviceDescription = 'Custom Service Form';
          this.serviceCost = '0.00';
          
          this.serviceInputs = questions.map((question, index) => `${index + 1}. ${question.question}`);
          
          this.serviceOutputs = ['Form submission data'];
        },
        error: (xhr) => {
          console.error("Error fetching form details:", xhr.responseText);
          this.serviceDescription = 'Could not load form details';
          this.serviceInputs = ['Error loading questions'];
          this.serviceOutputs = [];
        }
      });
    },

    get_workflow_information() {
      const workflow_id = this.$route.params.workflow_id;
      const token = localStorage.getItem("token");
      const get_workflow_information_api = `${API_BASE_URL}/verify_api/workflow_management_api/get_workflow_information_api/${workflow_id}`;

      $.ajax({
        url: get_workflow_information_api,
        method: "GET",
        contentType: "application/json",
        headers: {
          Authorization: `Token ${token}`,
        },
        beforeSend: () => {
          Swal.fire({
            text: "Loading ...",
            allowEscapeKey: false,
            allowOutsideClick: false,
            didOpen: () => {
              Swal.showLoading();
            },
          });
        },
        success: (response) => {
          Swal.close();
          this.workflow_data = response.data;
        },
        error: () => {
          Swal.close();
          Swal.fire({
            text: "Failed to fetch workflow information, try again later.",
            icon: "error",
          });
        },
      });
    },
    get_workflow_configurations() {
      const workflow_id = this.$route.params.workflow_id;
      const token = localStorage.getItem("token");
      const get_workflow_information = `${API_BASE_URL}/verify_api/workflow_management_api/get_workflow_data_api/${workflow_id}`;

      $.ajax({
        url: get_workflow_information,
        method: "GET",
        contentType: "application/json",
        headers: {
          Authorization: `Token ${token}`,
        },
        beforeSend: () => {
          Swal.fire({
            text: "Loading ...",
            allowEscapeKey: false,
            allowOutsideClick: false,
            didOpen: () => {
              Swal.showLoading();
            },
          });
        },
        success: (response) => {
          Swal.close();
          if (response.workflow_data) {
            const workflow = response.workflow_data[0];

            this.workflow_name = this.capitalize_first_letter(
              workflow.workflow_name || ""
            );
            this.workflow_description = this.capitalize_first_letter(
              workflow.workflow_description || ""
            );
            this.workflow_status = workflow.workflow_status || "";
            this.allow_multiple_onboarding =
              workflow.allow_multiple_onboarding || false;
            this.allow_batch_onboarding =
              workflow.allow_batch_onboarding || false;

            this.store_on_api = workflow.store_on_api || false;
            this.on_behalf_of_prepopulation =
              workflow.on_behalf_of_prepopulation || false;

          } else {
            Swal.fire({
              icon: "error",
              title: "Error",
              text: "Workflow data is missing.",
              confirmButtonText: "OK",
            });
          }
        },
        error: (error) => {
          const error_message =
            error?.responseJSON?.error || "Error fetching workflow details";
          Swal.fire({
            icon: "error",
            title: "Error",
            text: error_message,
            confirmButtonText: "OK",
          });
        },
      });
    },
    capitalize_first_letter(string) {
      return string.charAt(0).toUpperCase() + string.slice(1);
    },

    get_workflow_sub_workflows(){
      const token = localStorage.getItem('token');
      const base_url = API_BASE_URL;
      var workflow_id = this.$route.params.workflow_id;
      const get_customer_application_url = `${base_url}/verify_api/service_management_api/get_sub_workflows/`;
      $.ajax({
          method: 'GET',
          url: get_customer_application_url,
          contentType: 'application/json',
          headers: {
              Authorization: `Token ${token}`
          },
          data: {
              workflow_id: workflow_id
          },
          beforeSend: () => {
              Swal.fire({
                  text: 'Loading ...',
                  allowEscapeKey: false,
                  allowOutsideClick: false,
                  didOpen: () => {
                      Swal.showLoading();
                  }
              })
          },
          success: (response) => {
              Swal.close();
              
              if (Array.isArray(response.data.workflow_name)) {
                  this.workflow_name = response.data.workflow_name[0] || 'Workflow';
              } else {
                  this.workflow_name = response.data.workflow_name || 'Workflow';
              }
              
              if (response.status === 'success') {
                  var sub_workflows = response.data.sub_workflows;
                  var sub_external_workflows = response.data.sub_external_workflows;
                  
                
                  var combined_workflows = [];

          
                  if (Array.isArray(sub_workflows)) {
                      combined_workflows = combined_workflows.concat(
                          sub_workflows.map(workflow => ({
                              id: workflow.id,
                              workflow_id: workflow.workflow_id,
                              workflow_name: workflow.workflow.workflow_name,
                              service_name: workflow.service.service_name,
                              router_name: workflow.router_name,
                              external_link: '',
                              date_created: workflow.created_at,
                              is_active: workflow.is_active,
                              sub_workflow_type: 'internal' 
                          }))
                      );
                  }

            
                  if (Array.isArray(sub_external_workflows)) {
                      combined_workflows = combined_workflows.concat(
                          sub_external_workflows.map(workflow => ({
                              id: workflow.id,
                              workflow_id: '',
                              workflow_name: '',
                              service_name: workflow.service.service_name,
                              router_name: workflow.router_name,
                              external_url: workflow.external_link,
                              date_created: workflow.created_at,
                              is_active: workflow.is_active,
                              sub_workflow_type: 'external'
                          }))
                      );
                  }

                  this.workflows = combined_workflows;
              } else {
                  this.workflows = [];
              }
          }
      });
    },

    fetch_sub_workflows() {
        const token = localStorage.getItem('token');
        const base_url = API_BASE_URL;
        var workflow_id = this.$route.params.workflow_id;
        const url = `${base_url}/verify_api/service_management_api/check_sub_workflows/?workflow_id=${workflow_id}`;

        Swal.fire({
          text: 'Loading sub-workflows...',
          allowEscapeKey: false,
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          }
        });

        fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Token ${token}`
          }
        })
        .then(response => response.json())
        .then(data => {
          Swal.close();
          if (data.status === 'success') {

            // Extract the parent workflow name
            if (data.data.workflow_name) {
              if (Array.isArray(data.data.workflow_name)) {
                this.parentWorkflowName = data.data.workflow_name[0];
              } else {

                this.parentWorkflowName = data.data.workflow_name;

              }
            }

            // Get the list of completed workflows from localStorage
            // const completedWorkflows = JSON.parse(localStorage.getItem('completed_workflows') || '[]');

            var combined_workflows = [];

            if (Array.isArray(data.data.sub_workflows)) {
              const active_workflows = data.data.sub_workflows.filter(
                workflow => workflow.is_active === true
              );
              
              if (active_workflows.length > 0) {
                combined_workflows = combined_workflows.concat(
                  active_workflows.map(workflow => ({
                    id: workflow.id,
                    workflow_id: workflow.workflow_id,
                    workflow_name: workflow.workflow.workflow_name,
                    service_name: workflow.service.service_name,
                    router_name: workflow.router_name,
                    external_link: '',
                    date_created: workflow.created_at,
                    is_active: workflow.is_active,
                    sub_workflow_type: 'internal' 
                  }))
                );
              }
            }

          
            if (Array.isArray(data.data.sub_external_workflows)) {
              const activeExternalWorkflows = data.data.sub_external_workflows.filter(
                workflow => workflow.is_active === true
              );
              
              if (activeExternalWorkflows.length > 0) {
                combined_workflows = combined_workflows.concat(
                  activeExternalWorkflows.map(workflow => ({
                    id: workflow.id,
                    workflow_id: '',
                    workflow_name: '',
                    service_name: workflow.service.service_name,
                    router_name: workflow.router_name,
                    external_url: workflow.external_link,
                    date_created: workflow.created_at,
                    is_active: workflow.is_active,
                    sub_workflow_type: 'external'
                  }))
                );
              }
            }

            
            this.workflows = combined_workflows;
            
          } else {
            Swal.fire({
              icon: 'error',
              title: 'Unable to Load Sub-Workflows',
              text: 'Please try again later',
              timer: 3000,
              timerProgressBar: true,
              showConfirmButton: false
            });
          }
        })
        .catch(error => {
          Swal.close();
          Swal.fire({
            icon: 'error',
            title: 'Error Loading Sub-Workflows',
            text: error.message || 'An error occurred while loading sub-workflows',
            timer: 3000,
            timerProgressBar: true,
            showConfirmButton: false
          });
        });
      },
    decline_workflow() {
      var workflow_id = this.$route.params.workflow_id;
      const TOKEN = localStorage.getItem("token");
      const DECLINE_WORKFLOW_API = `${API_BASE_URL}/verify_api/workflow_management_api/decline_workflow_api/`;
      var data = {
        workflow_id: workflow_id,
      };

      Swal.fire({
        title: "Decline Workflow",
        input: "textarea",
        inputLabel: "Reason for Decline",
        inputPlaceholder: "Type your reason here...",
        inputAttributes: {
          "aria-label": "Type your reason here",
        },
        showCancelButton: true,
        confirmButtonText: "Submit",
        cancelButtonText: "Cancel",
      }).then((result) => {
        if (result.isConfirmed) {
          var reason = result.value;
          data.reason = reason;

          $.ajax({
            url: DECLINE_WORKFLOW_API,
            method: "POST",
            contentType: "application/json",
            data: JSON.stringify(data),
            headers: {
              Authorization: `Token ${TOKEN}`,
            },
            beforeSend: () => {
              Swal.fire({
                text: "Declining workflow ...",
                allowEscapeKey: false,
                allowOutsideClick: false,
                didOpen: () => {
                  Swal.showLoading();
                },
              });
            },
            success: () => {
              Swal.close();
              Swal.fire({
                text: "Workflow declined successfully.",
                icon: "success",
              });
              this.get_workflow_information();
            },
            error: () => {
              Swal.close();
              Swal.fire({
                text: "Failed to decline workflow, try again later.",
                icon: "error",
              });
            },
          });
        }
      });
    },
    approve_workflow() {
      var workflow_id = this.$route.params.workflow_id;
      const TOKEN = localStorage.getItem("token");
      const APPROVE_WORKFLOW_API = `${API_BASE_URL}/verify_api/workflow_management_api/approve_workflow_api/`;
      var data = {
        workflow_id: workflow_id,
      };

      Swal.fire({
        title: "Are you sure?",
        text: "Do you really want to approve this workflow?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, approve it!",
        cancelButtonText: "No, cancel!",
      }).then((result) => {
        if (result.isConfirmed) {
          $.ajax({
            url: APPROVE_WORKFLOW_API,
            method: "POST",
            contentType: "application/json",
            data: JSON.stringify(data),
            headers: {
              Authorization: `Token ${TOKEN}`,
            },
            beforeSend: () => {
              Swal.fire({
                text: "Approving workflow ...",
                allowEscapeKey: false,
                allowOutsideClick: false,
                didOpen: () => {
                  Swal.showLoading();
                },
              });
            },
            success: () => {
              Swal.close();
              Swal.fire({
                text: "Workflow approved successfully.",
                icon: "success",
              });
              this.get_workflow_information();
            },
            error: () => {
              Swal.close();
              Swal.fire({
                text: "Failed to approve workflow, try again later.",
                icon: "error",
              });
            },
          });
        }
      });
    },
    capitilize_string(string) {
      return string.charAt(0).toUpperCase() + string.slice(1);
    },
    exportWorkflow() {
      const workflow_id = this.$route.params.workflow_id;
      const token = localStorage.getItem("token");
      
      this.exporting = true;
      
      $.ajax({
        url: `${API_BASE_URL}/verify_api/workflow_management_api/export_workflow/${workflow_id}/`,
        method: "GET",
        contentType: "application/json",
        headers: {
          Authorization: `Token ${token}`,
        },
        success: (response) => {
          this.exporting = false;
          const { showSuccess } = useToast();
          
          // Create and download the JSON file
          const workflowName = response.workflow_name || `workflow_${workflow_id}`;
          const sanitizedName = workflowName.replace(/[^a-z0-9]/gi, '_').toLowerCase();
          const blob = new Blob([JSON.stringify(response, null, 2)], { type: 'application/json' });
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `${sanitizedName}_export.json`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
          
          showSuccess('Export Successful', `Workflow "${workflowName}" exported successfully`);
        },
        error: (xhr) => {
          this.exporting = false;
          const { showError } = useToast();
          
          let errorMessage = 'Failed to export workflow. Please try again.';
          if (xhr.responseJSON) {
            errorMessage = xhr.responseJSON.message || xhr.responseJSON.error || errorMessage;
          } else if (xhr.status === 404) {
            errorMessage = 'Workflow not found.';
          } else if (xhr.status === 403) {
            errorMessage = 'You do not have permission to export this workflow.';
          }
          
          showError('Export Failed', errorMessage);
        },
      });
    },
    
    async load_apexcharts() {
      if (typeof window.ApexCharts !== "undefined") {
        return; // Already loaded
      }

      try {
        const load_script = (src) =>
          new Promise((resolve, reject) => {
            if (document.querySelector(`script[src="${src}"]`)) {
              resolve();
              return;
            }
            const s = document.createElement("script");
            s.src = src;
            s.async = false;
            s.onload = resolve;
            s.onerror = reject;
            document.head.appendChild(s);
          });

        await load_script("https://cdn.jsdelivr.net/npm/apexcharts");
      } catch (error) {
        console.warn("Failed to load ApexCharts:", error);
      }
    },
  },
  mounted() {
    if (!validateToken(this.$router)) {
        return;
    }
    this.get_workflow_information();
    this.get_workflow_configurations();
    this.get_workflow_sub_workflows();
    this.load_apexcharts();
  },
};